import React, { createContext, useContext, useEffect, useRef, useSyncExternalStore } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabaseHealth, type HealthStatus } from "@/lib/supabaseHealth";
import { ServiceUnavailable } from "./ServiceUnavailable";

interface SupabaseHealthContextValue {
  status: HealthStatus;
  isDown: boolean;
}

const SupabaseHealthContext = createContext<SupabaseHealthContextValue>({
  status: 'healthy',
  isDown: false,
});

// Adapter for useSyncExternalStore
let currentStatus: HealthStatus = supabaseHealth.getStatus();

function subscribeToHealth(callback: () => void): () => void {
  return supabaseHealth.subscribe((status) => {
    currentStatus = status;
    callback();
  });
}

function getSnapshot(): HealthStatus {
  return currentStatus;
}

export function SupabaseHealthProvider({ children }: { children: React.ReactNode }) {
  const status = useSyncExternalStore(subscribeToHealth, getSnapshot, getSnapshot);
  const isDown = status === 'down';
  const queryClient = useQueryClient();
  const prevStatusRef = useRef(status);

  // Ensure the monitor starts polling when the provider mounts
  useEffect(() => {
    const unsub = supabaseHealth.subscribe(() => {});
    return unsub;
  }, []);

  // Refetch all queries when recovering from an outage
  useEffect(() => {
    const prev = prevStatusRef.current;
    prevStatusRef.current = status;

    if (prev === 'down' && status === 'healthy') {
      queryClient.invalidateQueries();
    }
  }, [status, queryClient]);

  return (
    <SupabaseHealthContext.Provider value={{ status, isDown }}>
      {children}
      {isDown && (
        <div className="fixed inset-0 z-[9999]">
          <ServiceUnavailable />
        </div>
      )}
    </SupabaseHealthContext.Provider>
  );
}

export function useSupabaseHealth(): SupabaseHealthContextValue {
  return useContext(SupabaseHealthContext);
}
